import { RepoContext, type Repo } from "@automerge/react";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect, useState } from "react";
import { createRoot, type Root } from "react-dom/client";

import { Toaster } from "@/components/ui/sonner";

import "../index.css";
// Side-effect import: attaches the `beforeinstallprompt` listener at startup so
// the one-shot event is captured even if it fires before the workspace route
// (and its `InstallButton`) has loaded.
import "./pwa/install";
// Side-effect import: listens for `vite:preloadError` so a stale bundle that
// can't fetch a lazily-loaded (non-precached) chunk after a deploy takes the
// waiting service worker and reloads into the fresh build, instead of
// dead-ending. Imported at startup so it's armed before any route lazy-loads.
import "./pwa/stale-chunk-reload";
import { repoReady } from "./automerge/repo";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { persistOptions, queryClient } from "./query-client";
import { router } from "./router";

const root = document.getElementById("root");
if (!root) throw new Error("missing #root");

// The repo arrives async (wasm compile — see automerge/repo.ts); render
// immediately and let the workspace/embed gates cover the gap.
function AppRoot() {
  const [repo, setRepo] = useState<Repo | null>(null);
  useEffect(() => {
    repoReady.then(setRepo, (err: unknown) => {
      // Without the wasm the workspace can never load; leave the gate up.
      console.error("Automerge wasm failed to initialize", err);
    });
  }, []);
  return (
    <RepoContext.Provider value={repo}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
      >
        <RouterProvider router={router} />
        <Toaster />
        <UpdatePrompt />
      </PersistQueryClientProvider>
    </RepoContext.Provider>
  );
}

// PersistQueryClientProvider rehydrates the boot cache from localStorage before
// queries run, so the workspace shell can render from last-known state on a cold
// launch instead of blocking on the network. localStorage is synchronous, so
// restoration is effectively instant.
// ONE root per container, however many times this module is evaluated. A dev
// server can run the entry twice (an HMR update that propagates to it, a
// service worker serving a shell that loads the module under a second URL),
// and a second `createRoot` on the same node leaves two roots editing the
// same DOM. That surfaces as `removeChild`/`insertBefore` NotFoundError
// thrown from inside React on whatever route happens to be mounted, which
// reads like a bug in that route and isn't one. Rendering into the existing
// root is what React itself tells you to do here.
const container = root as HTMLElement & { reactRoot?: Root };
(container.reactRoot ??= createRoot(root)).render(
  <StrictMode>
    <AppRoot />
  </StrictMode>,
);
